import { useEffect } from 'react';

export function isTyping(e: KeyboardEvent) {
  const el = e.target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

export interface HotkeyHandlers {
  onPalette: () => void;
  onAsk?: () => void;
  onSlash?: () => void;
  onGo: (key: string) => void;
  onHelp?: () => void;
  onSidebar?: () => void;
  onPanel?: () => void;
}

/**
 * Global keyboard shortcuts: ⌘K / Ctrl+K toggles the launcher, ⌘J asks an agent, `/` opens the
 * launcher when not typing, `g <key>` sequences navigate, `?` shows help, `[` / `]` toggle side panels.
 */
export function useHotkeys(handlers: HotkeyHandlers) {
  useEffect(() => {
    let pendingG = 0;
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      const key = e.key.toLowerCase();
      if (mod && !e.altKey && !e.shiftKey && key === 'k') {
        e.preventDefault();
        handlers.onPalette();
        return;
      }
      if (mod && !e.altKey && !e.shiftKey && key === 'j' && handlers.onAsk) {
        e.preventDefault();
        handlers.onAsk();
        return;
      }
      if (e.defaultPrevented || isTyping(e) || mod || e.altKey) return;
      if (pendingG && Date.now() - pendingG < 1200) {
        pendingG = 0;
        handlers.onGo(key);
        return;
      }
      if (e.key === 'g') pendingG = Date.now();
      else if (e.key === '?') handlers.onHelp?.();
      else if (e.key === '/' && handlers.onSlash) {
        e.preventDefault();
        handlers.onSlash();
      } else if (e.key === '[') handlers.onSidebar?.();
      else if (e.key === ']') handlers.onPanel?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handlers]);
}
